import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const useInactivityLock = (timeoutMinutes = 15) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  const inactivityTime = timeoutMinutes * 60 * 1000;
  const warningSeconds = 120;
  const warningTime = Math.max(inactivityTime - warningSeconds * 1000, 0);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const closeWarning = useCallback(() => {
    window.dispatchEvent(new CustomEvent('inactivityWarningClose'));
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (!user) return;

    clearAllTimers();
    closeWarning();

    warningTimeoutRef.current = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('inactivityWarning', {
          detail: { timeLeft: warningSeconds },
        })
      );
    }, warningTime);

    timeoutRef.current = setTimeout(() => {
      closeWarning();
      logout();
      navigate('/login', { replace: true });
      window.dispatchEvent(new CustomEvent('inactivityLogout'));
    }, inactivityTime);
  }, [user, clearAllTimers, closeWarning, warningTime, inactivityTime, logout, navigate]);

  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    closeWarning();
  }, [clearAllTimers, closeWarning]);

  useEffect(() => {
    if (!user) {
      clearAllTimers();
      closeWarning();
      return;
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    window.addEventListener('userLogout', handleLogout);
    window.addEventListener('resetInactivityTimer', resetInactivityTimer);

    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener('userLogout', handleLogout);
      window.removeEventListener('resetInactivityTimer', resetInactivityTimer);
      clearAllTimers();
    };
  }, [user, handleUserActivity, resetInactivityTimer, handleLogout, clearAllTimers, closeWarning]);
};

export default useInactivityLock;